import debounce from "lodash.debounce";
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import useAxios from "./useAxios";

const PAGE_SIZE = 20000;

/**
 * Shared batch-detail (single batch's line list) state: fetch, and the
 * "Latest Only" filter, synced to the URL query string. Consolidates what
 * used to be duplicated per-page state + fetch effects across every
 * `*Batch.js` page (one per carrier x operation).
 */
const useBatchDetailState = (apiConfig, { client, extraParams } = {}) => {
  const { batch_id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState([]);
  const [metaData, setMetaData] = useState({
    page: 1,
    page_size: PAGE_SIZE,
    total: 1,
  });
  const [response, call, loading] = useAxios({ ...apiConfig });

  const latestOnly = searchParams.get("latest_only") === "true";

  const debouncedFetchData = debounce((query) => {
    call(query);
  }, 500);

  useEffect(() => {
    debouncedFetchData({
      params: {
        client,
        page_size: PAGE_SIZE,
        batch_id,
        ...extraParams,
        ...(latestOnly ? { latest_only: true } : {}),
      },
    });

    return () => {
      debouncedFetchData.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batch_id, client, latestOnly, JSON.stringify(extraParams || {})]);

  useEffect(() => {
    if (response && response.status) {
      setMetaData({
        page: response.data.page,
        page_size: response.data.page_size,
        total: response.data.total,
      });
      setData(response.data.data);
    }
  }, [response]);

  const handleLatestOnlyChange = () => {
    const next = new URLSearchParams(searchParams);
    if (latestOnly) {
      next.delete("latest_only");
    } else {
      next.set("latest_only", "true");
    }
    setSearchParams(next);
  };

  return {
    batch_id,
    data,
    loading,
    metaData,
    response,
    latestOnly,
    handleLatestOnlyChange,
  };
};

export default useBatchDetailState;
